import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Ellipsis, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * A cog that opens a short menu of what its viewer may do to what it sits
 * on — a space, an event, one of its parts. One way of running things,
 * everywhere: the controls stay out of the way of what a member does on the
 * same page. shadcn's dropdown menu: the arrow keys move through it, Escape
 * or a tap elsewhere closes it, and so does choosing an item (`close` is
 * handed to the items too, for one that opens a dialog). Not modal, so a
 * dialog opened from an item takes the page as its own. What it offers is
 * presentation; the api refuses whatever the caller may not do.
 */
export function CogMenu({
  label,
  badge = 0,
  compact = false,
  more = false,
  children,
}: {
  /** What the cog is called to a screen reader: "Space menu", "Part B menu". */
  label: string;
  /** A count shown on the cog itself, read as part of its label by the caller. */
  badge?: number;
  /** A smaller cog, for a card; otherwise it is a page's, as tall as its bar's buttons (0078). */
  compact?: boolean;
  /** A bare "…" in place of the outlined cog: a feed's event card's (0083). */
  more?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant={more ? "ghost" : "outline"}
          size={badge > 0 ? (compact ? "sm" : "lg") : compact ? "icon-sm" : "icon-lg"}
          aria-label={label}
          className={cn("shrink-0", more && "text-muted-foreground")}
        >
          {more ? <Ellipsis aria-hidden="true" /> : <Settings aria-hidden="true" />}
          {badge > 0 && <Badge aria-hidden="true">{badge}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {children(() => setOpen(false))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A page in a cog's menu, with its count read as "Members (3)". */
export function MenuLink({
  to,
  label,
  count,
  highlight = false,
}: {
  to: string;
  label: string;
  count?: number;
  highlight?: boolean;
}) {
  return (
    <DropdownMenuItem asChild>
      <Link to={to} aria-label={count === undefined ? undefined : `${label} (${count})`} className="justify-between">
        {label}
        {count !== undefined &&
          (highlight ? <Badge>{count}</Badge> : <span className="text-xs text-muted-foreground">{count}</span>)}
      </Link>
    </DropdownMenuItem>
  );
}

/** Something done from a cog's menu; `danger` for what cannot be undone. */
export function MenuButton({
  onClick,
  danger = false,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <DropdownMenuItem variant={danger ? "destructive" : "default"} onSelect={onClick}>
      {children}
    </DropdownMenuItem>
  );
}
